import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface ActiveSuspension {
  id: string;
  reason: string;
  suspended_at: string;
  expires_at: string | null;
  is_permanent: boolean;
}

/**
 * Resolves the signed-in user's active suspension (if any) via the
 * `get_active_suspension` RPC. Admins are never gated.
 */
export function useSuspensionStatus() {
  const { user, loading: authLoading } = useAuth();
  const [suspension, setSuspension] = useState<ActiveSuspension | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function check() {
      if (authLoading) return;
      if (!user) {
        if (!cancelled) {
          setSuspension(null);
          setLoading(false);
        }
        return;
      }

      const [{ data: suspensionRows }, { data: adminRow }] = await Promise.all([
        supabase.rpc('get_active_suspension', { user_id_input: user.id }),
        supabase.from('user_roles').select('role').eq('user_id', user.id).eq('role', 'admin').maybeSingle(),
      ]);

      if (cancelled) return;

      // Admins are never locked out of their own tooling.
      if (adminRow) {
        setSuspension(null);
        setLoading(false);
        return;
      }

      const active = Array.isArray(suspensionRows) ? (suspensionRows[0] as ActiveSuspension | undefined) : undefined;
      setSuspension(active ?? null);
      setLoading(false);
    }

    check();
    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return { suspension, loading: loading || authLoading };
}